import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  AbstractControl,
  FormArray,
} from "@angular/forms";
import { forwardRef, Component, OnInit, Input, ViewChild, ElementRef, ChangeDetectorRef } from "@angular/core";
import {
  InvoiceCashDesk,
  InvoiceCashDeskTotals,
} from "../../../../model/invoice-cash-desk";
import { FilterService } from "../../../../services/filter.service";
import { FilterCompleteService } from "../../../../filter-complete/filter-complete";
import { TaxpayerType } from "../../../../model/taxpayer-type";
import { InvoiceLabelType } from "../../../../model/invoice-label-type";
import { InvoiceCashDeskService } from "../../../../services/invoice-cash-desk/invoice-cash-desk.service";
import { InvoiceTax } from "../../../../model/invoice-tax";
import { BehaviorSubject } from "rxjs";
import { CashDeskUserOpen } from "../../../../model/cash-desk-user-open";
import { AuthService } from "../../../../services/auth.service";
import {
  invoiceCashDeskValidator,
  validPaymentCard,
  validPaymentCheck,
  validPaymentTransfer,
} from "../../../../validators/cash-desk/invoice-cash-desk.validator";
import { BankFilter } from "../../../../model/bank-filter";
import { CreditCardFilter } from "../../../../model/credit-card-filter";
import { InvoiceCashDeskPayment } from "../../../../model/invoice-cash-desk-payment";
import { ServiceFilter } from "../../../../model/service-filter";
import { NotificationToastService } from "../../../../services/notification-toast.service";
import { InvoicePoint } from "../../../../model/invoice-point";
import { InvoicePointSelectManualType } from "../../../../model/invoice-point-select-manual-type";
import { InvoiceLabelService } from "../../../../services/invoice-label/invoice-label.service";
import { VirtualWalletFilter } from "../../../../model/virtual-wallet-filter";
import { CentersService } from "../../../../services/centers/centers.service";

const PAYMENT_METHOD_ID_CASH = 1;
const PAYMENT_METHOD_ID_CREDIT_CARD = 2;
const PAYMENT_METHOD_ID_DEBIT_CARD = 3;
const PAYMENT_METHOD_ID_CHECK = 4;
const PAYMENT_METHOD_ID_WIRE_TRANSFER = 6;
const PAYMENT_METHOD_ID_QR_LINK = 5;

export const INVOICE_CASH_DESK_EDIT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InvoiceCashDeskControlComponent),
  multi: true,
};

@Component({
  selector: "app-invoice-cash-desk-edit-control",
  providers: [INVOICE_CASH_DESK_EDIT_CONTROL_VALUE_ACCESSOR],
  templateUrl: "./invoice-cash-desk-edit-control.component.html",
})
export class InvoiceCashDeskControlComponent
  implements OnInit, ControlValueAccessor
{
  form: FormGroup;
  onChange;
  filterCompleteTaxpayerTypes: FilterCompleteService<TaxpayerType>;
  filterCompleteInvoiceLabelTypes: FilterCompleteService<InvoiceLabelType>;
  filterCompleteBanksPaymentDebitCard: FilterCompleteService<BankFilter>;
  filterCompleteBanksPaymentCreditCard: FilterCompleteService<BankFilter>;
  filterCompleteBanksPaymentCheck: FilterCompleteService<BankFilter>;

  filterCompleteVirtualWalletPaymentDebitCard: FilterCompleteService<VirtualWalletFilter>;
  filterCompleteVirtualWalletPaymentCreditCard: FilterCompleteService<VirtualWalletFilter>;
  filterCompleteVirtualWalletPaymentCheck: FilterCompleteService<VirtualWalletFilter>;
  filterCompleteVirtualWalletPaymentTransfer: FilterCompleteService<VirtualWalletFilter>;
  filterCompleteVirtualWalletPaymentQrLink: FilterCompleteService<VirtualWalletFilter>;

  filterCompleteCreditCardsPaymentDebitCard: FilterCompleteService<CreditCardFilter>;
  filterCompleteCreditCardsPaymentCreditCard: FilterCompleteService<CreditCardFilter>;
  filterCompleteService: FilterCompleteService<ServiceFilter>;
  filterCompleteServiceManual: FilterCompleteService<ServiceFilter>;
  filterCompleteInvoicePoint: FilterCompleteService<InvoicePoint>;
  invoicePointSelectManualTypes: InvoicePointSelectManualType[] = [];
  bankFilters: BankFilter[] = [];
  virtualWalletFilters: VirtualWalletFilter[] = [];
  creditCardFilters: CreditCardFilter[] = [];

  taxpayerTypes: TaxpayerType[] = [];
  displayInvoiceTaxesColumns = ["TaxTypeName", "Aliquot", "Amount"];
  invoiceTaxesDataSource = new BehaviorSubject<AbstractControl[]>([]);
  @Input() cashdeskuseropen: CashDeskUserOpen;
  invoicePointName: string;

  isVisiblePaymentDebitCard: boolean;
  isVisiblePaymentCreditCard: boolean;
  isVisiblePaymentCheck: boolean;
  isVisiblePaymentWireTransfer: boolean;
  isVisiblePaymentQrLink = false;
  loading: boolean;
  canSelectAllInvoicePoint: boolean;

  isPaymentMethodCashEnabled = true;
  isPaymentMethodDebitCardEnabled = true;
  isPaymentMethodCreditCardEnabled = true;
  isPaymentMethodCheckEnabled = true;
  isPaymentMethodWireTransferEnabled = true;
  isPaymentMethodQrLinkEnabled = true;
  private focusedPaymentMethodId: number | null = null;
  private paymentMethodsLoaded = false;
  private invoiceValueLoaded = false;

  @ViewChild("cashPaymentAmountInput", { static: false }) cashPaymentAmountInput: ElementRef;
  @ViewChild("debitCardPaymentAmountInput", { static: false }) debitCardPaymentAmountInput: ElementRef;
  @ViewChild("creditCardPaymentAmountInput", { static: false }) creditCardPaymentAmountInput: ElementRef;
  @ViewChild("checkPaymentAmountInput", { static: false }) checkPaymentAmountInput: ElementRef;
  @ViewChild("wireTransferPaymentAmountInput", { static: false }) wireTransferPaymentAmountInput: ElementRef;
  @ViewChild("qrLinkPaymentAmountInput", { static: false }) qrLinkPaymentAmountInput!: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    private invoiceCashDeskService: InvoiceCashDeskService,
    private authService: AuthService,
    private notificationToastService: NotificationToastService,
    private invoiceLabelService: InvoiceLabelService,
    private filterService: FilterService,
    private centersService: CentersService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.form = this.formBuilder.group(
      {
        Amount: [
          { value: "", disabled: !this.invoiceCashDeskService.canEditAmount() },
        ],
        TaxpayerType: [
          { value: null, disabled: !this.invoiceCashDeskService.canEdit() },
        ],
        InvoiceLabelType: [
          { value: null, disabled: !this.invoiceCashDeskService.canEdit() },
        ],
        ClientSearch: [
          { value: null, disabled: !this.invoiceCashDeskService.canEdit() },
        ],
        BusinessName: [
          { value: "", disabled: !this.invoiceCashDeskService.canEdit() },
        ],
        Service: [
          { value: null, disabled: !this.invoiceCashDeskService.canEdit() },
        ],
        ServiceManual: [
          { value: null, disabled: !this.invoiceCashDeskService.canEdit() },
        ],
        InvoicePoint: [
          { value: null, disabled: !this.invoiceCashDeskService.canEdit() },
        ],
        InvoicePointManual: [
          { value: null, disabled: !this.invoiceCashDeskService.canEdit() },
        ],
        InvoicePointSelectManualType: [
          { value: null, disabled: !this.invoiceCashDeskService.canEdit() },
        ],
        Document: [
          { value: "", disabled: !this.invoiceCashDeskService.canEdit() },
        ],
        IsManual: [{ value: !this.invoiceCashDeskService.isPatient() }],
        Description: [""],
        InvoiceTypeId: [null],
        InvoiceId: [null],
        InvoiceParentId: [null],
        Number: [null],
        Observation: [null],
        PatientId: [null],
        ValorizationId: [null],
        Address: [null],
        MedicalAppointmentId: [null],
        Atention: [null],
        TaxNumber: [null],
        Cuit: [null],
        EquipmentId: [null],
        SectorId: [null],
        CashDeskInvoicePointId: [null],
        InvoiceNumberBookId: [null],
        InvoiceNumberBookTypeId: [null],
        CashDeskOpenId: [null],
        InvoiceCashDeskItems: [null],
        VirtualWalletTransactionId: [null],
        VirtualWalletOriginalAmount: [null],
        InvoiceCashDeskPaymentCash: this.getPaymentCashFormGroup(),
        InvoiceCashDeskPaymentDebitCard: this.getPaymentCardFormGroup(),
        InvoiceCashDeskPaymentCreditCard: this.getPaymentCardFormGroup(),
        InvoiceCashDeskPaymentCheck: this.getPaymentCheckFormGroup(),
        InvoiceCashDeskPaymentWireTransfer: this.getPaymentTransferFormGroup(),
        InvoiceCashDeskPaymentQrLink: this.getPaymentTransferFormGroup(),
        InvoiceTaxes: this.formBuilder.array([]),
      },
      { validator: invoiceCashDeskValidator.bind(this) }
    );

    this.filterCompleteTaxpayerTypes = new FilterCompleteService<TaxpayerType>(
      this.taxpayerTypes,
      this.form.controls.TaxpayerType
    );
    this.filterCompleteInvoiceLabelTypes =
      new FilterCompleteService<InvoiceLabelType>(
        [],
        this.form.controls.InvoiceLabelType
      );
    this.filterCompleteBanksPaymentDebitCard =
      new FilterCompleteService<BankFilter>(
        this.bankFilters,
        this.invoiceCashDeskPaymentDebitCard.BankFilter
      );
    this.filterCompleteBanksPaymentCreditCard =
      new FilterCompleteService<BankFilter>(
        this.bankFilters,
        this.invoiceCashDeskPaymentCreditCard.BankFilter
      );
    this.filterCompleteBanksPaymentCheck =
      new FilterCompleteService<BankFilter>(
        this.bankFilters,
        this.invoiceCashDeskPaymentCheck.BankFilter
      );
    this.filterCompleteCreditCardsPaymentDebitCard =
      new FilterCompleteService<CreditCardFilter>(
        this.creditCardFilters,
        this.invoiceCashDeskPaymentDebitCard.CreditCardFilter
      );
    this.filterCompleteCreditCardsPaymentCreditCard =
      new FilterCompleteService<CreditCardFilter>(
        this.creditCardFilters,
        this.invoiceCashDeskPaymentCreditCard.CreditCardFilter
      );
    this.filterCompleteService = new FilterCompleteService<ServiceFilter>(
      [],
      this.form.controls.Service
    );
    this.filterCompleteServiceManual = new FilterCompleteService<ServiceFilter>(
      [],
      this.form.controls.ServiceManual
    );
    this.filterCompleteInvoicePoint = new FilterCompleteService<InvoicePoint>(
      [],
      this.form.controls.InvoicePointManual
    );

    this.filterCompleteVirtualWalletPaymentDebitCard =
      new FilterCompleteService<VirtualWalletFilter>(
        this.virtualWalletFilters,
        this.invoiceCashDeskPaymentDebitCard.VirtualWalletFilter
      );
    this.filterCompleteVirtualWalletPaymentCreditCard =
      new FilterCompleteService<VirtualWalletFilter>(
        this.virtualWalletFilters,
        this.invoiceCashDeskPaymentDebitCard.VirtualWalletFilter
      );
    this.filterCompleteVirtualWalletPaymentCheck =
      new FilterCompleteService<VirtualWalletFilter>(
        this.virtualWalletFilters,
        this.invoiceCashDeskPaymentDebitCard.VirtualWalletFilter
      );
    this.filterCompleteVirtualWalletPaymentTransfer =
      new FilterCompleteService<VirtualWalletFilter>(
        this.virtualWalletFilters,
        this.invoiceCashDeskPaymentWireTransfer.VirtualWalletFilter
      );
    this.filterCompleteVirtualWalletPaymentQrLink =
      new FilterCompleteService<VirtualWalletFilter>(
        this.virtualWalletFilters,
        this.invoiceCashDeskPaymentQrLink.VirtualWalletFilter
      );

    this.form.valueChanges.subscribe(() => {
      this.changeInvoiceCashDesk();
    });

    this.form.controls.Amount.valueChanges.subscribe(() => {
      this.updateFocusedPaymentAmount();
    });

    this.form.controls.ClientSearch.valueChanges.subscribe((clientSearch) => {
      if (clientSearch) {
        this.form.patchValue({ BusinessName: clientSearch.BusinessName });
      } else if (this.invoiceCashDesk()) {
        this.form.patchValue({
          BusinessName: this.invoiceCashDesk().BusinessName,
        });
      }
    });
  }

  changeInvoiceCashDesk(): void {
    const invoiceCashDesk = this.invoiceCashDesk();
    if (this.onChange) {
      this.onChange(invoiceCashDesk);
    }
  }

  invoiceCashDesk(): InvoiceCashDesk {
    if (this.form) {
      return <InvoiceCashDesk>this.form.getRawValue();
    } else {
      return null;
    }
  }

  get invoiceCashDeskPaymentCash() {
    return (<FormGroup>this.form.get("InvoiceCashDeskPaymentCash")).controls;
  }
  get invoiceCashDeskPaymentDebitCard() {
    return (<FormGroup>this.form.get("InvoiceCashDeskPaymentDebitCard"))
      .controls;
  }
  get invoiceCashDeskPaymentCreditCard() {
    return (<FormGroup>this.form.get("InvoiceCashDeskPaymentCreditCard"))
      .controls;
  }
  get invoiceCashDeskPaymentCheck() {
    return (<FormGroup>this.form.get("InvoiceCashDeskPaymentCheck")).controls;
  }
  get invoiceCashDeskPaymentWireTransfer() {
    return (<FormGroup>this.form.get("InvoiceCashDeskPaymentWireTransfer"))
      .controls;
  }
  get invoiceCashDeskPaymentQrLink() {
    return (<FormGroup>this.form.get("InvoiceCashDeskPaymentQrLink"))
      .controls;
  }
  get invoiceTaxes() {
    return <FormArray>this.form.get("InvoiceTaxes");
  }
  get filteredServiceOptions() {
    return this.filterCompleteService
      ? this.filterCompleteService.filteredOptions
      : [];
  }

  get filteredBankPaymentDebitCardOptions() {
    return this.filterCompleteBanksPaymentDebitCard
      ? this.filterCompleteBanksPaymentDebitCard.filteredOptions
      : [];
  }
  get filteredBankPaymentCreditCardOptions() {
    return this.filterCompleteBanksPaymentCreditCard
      ? this.filterCompleteBanksPaymentCreditCard.filteredOptions
      : [];
  }
  get filteredBankPaymentCheckOptions() {
    return this.filterCompleteBanksPaymentCheck
      ? this.filterCompleteBanksPaymentCheck.filteredOptions
      : [];
  }

  get filteredVirtualWalletPaymentDebitCardOptions() {
    return this.filterCompleteVirtualWalletPaymentDebitCard
      ? this.filterCompleteVirtualWalletPaymentDebitCard.filteredOptions
      : [];
  }
  get filteredVirtualWalletPaymentCreditCardOptions() {
    return this.filterCompleteVirtualWalletPaymentCreditCard
      ? this.filterCompleteVirtualWalletPaymentCreditCard.filteredOptions
      : [];
  }
  get filteredVirtualWalletPaymentCheckOptions() {
    return this.filterCompleteVirtualWalletPaymentCheck
      ? this.filterCompleteVirtualWalletPaymentCheck.filteredOptions
      : [];
  }
  get filteredVirtualWalletPaymentTransferOptions() {
    return this.filterCompleteVirtualWalletPaymentTransfer
      ? this.filterCompleteVirtualWalletPaymentTransfer.filteredOptions
      : [];
  }
  get filteredVirtualWalletPaymentQrLinkOptions() {
    return this.filterCompleteVirtualWalletPaymentQrLink
      ? this.filterCompleteVirtualWalletPaymentQrLink.filteredOptions
      : [];
  }

  get filteredCreditCargPaymentDebitCardOptions() {
    return this.filterCompleteCreditCardsPaymentDebitCard
      ? this.filterCompleteCreditCardsPaymentDebitCard.filteredOptions
      : [];
  }
  get filteredCreditCargPaymentCreditCardOptions() {
    return this.filterCompleteCreditCardsPaymentCreditCard
      ? this.filterCompleteCreditCardsPaymentCreditCard.filteredOptions
      : [];
  }
  get filteredTaxpayerTypesOptions() {
    return this.filterCompleteTaxpayerTypes
      ? this.filterCompleteTaxpayerTypes.filteredOptions
      : [];
  }
  get filteredInvoiceLabelTypesOptions() {
    return this.filterCompleteInvoiceLabelTypes
      ? this.filterCompleteInvoiceLabelTypes.filteredOptions
      : [];
  }
  get filteredInvoicePointOptions() {
    return this.filterCompleteInvoicePoint
      ? this.filterCompleteInvoicePoint.filteredOptions
      : [];
  }
  get filteredServiceManualOptions() {
    return this.filterCompleteServiceManual
      ? this.filterCompleteServiceManual.filteredOptions
      : [];
  }
  get f() {
    return this.form.controls;
  }
  get errors() {
    return this.form.errors;
  }
  get canEditInvoicePoint() {
    return this.invoiceCashDeskService.canEditInvoicePoint();
  }
  get isPatient() {
    return this.invoiceCashDeskService.isPatient();
  }
  get isManualInvoicePointSelectType() {
    if (
      this.form &&
      !this.isPatient &&
      this.form.controls.InvoicePointSelectManualType.value
    ) {
      return (
        (<InvoicePointSelectManualType>(
          this.form.controls.InvoicePointSelectManualType.value
        )).Id === 2
      );
    } else {
      return false;
    }
  }
  get isVisiblePaymentDetails() {
    return (
      this.isVisiblePaymentDebitCard ||
      this.isVisiblePaymentCreditCard ||
      this.isVisiblePaymentCheck ||
      this.isVisiblePaymentWireTransfer ||
      this.isVisiblePaymentQrLink
    );
  }

  async writeValue(obj: InvoiceCashDesk) {
    if (obj) {
      let taxpayerType: TaxpayerType;

      if (this.taxpayerTypes.length === 0) {
        this.taxpayerTypes = await this.filterService.getTaxpayerType();
        this.filterCompleteTaxpayerTypes =
          new FilterCompleteService<TaxpayerType>(
            this.taxpayerTypes,
            this.form.controls.TaxpayerType
          );
      }

      if (obj.TaxpayerType && this.taxpayerTypes) {
        taxpayerType = this.taxpayerTypes.find(
          (t) => t.Id === obj.TaxpayerType.Id
        );
        await this.taxpayerTypeSelected(taxpayerType);
      }

      this.form.controls.Amount.setValue(obj.Amount.toFixed(2), { emitEvent: false });
      this.form.patchValue({
        TaxpayerType: taxpayerType,
        InvoiceLabelType: obj.InvoiceLabelType,
        ClientSearch: obj.ClientSearch,
        BusinessName: obj.BusinessName,
        Description: obj.Description,
        InvoiceTypeId: obj.InvoiceTypeId,
        InvoiceId: obj.InvoiceId,
        InvoiceParentId: obj.InvoiceParentId,
        Number: obj.Number,
        Observation: obj.Observation,
        PatientId: obj.PatientId,
        Address: obj.Address,
        MedicalAppointmentId: obj.MedicalAppointmentId,
        Atention: obj.Atention,
        TaxNumber: obj.TaxNumber,
        EquipmentId: obj.EquipmentId,
        SectorId: this.authService.getSectorId(),
        InvoiceCashDeskItems: obj.InvoiceCashDeskItems,
        CashDeskInvoicePointId: obj.CashDeskInvoicePointId,
        InvoiceNumberBookId: obj.InvoiceNumberBookId,
        InvoiceNumberBookTypeId: obj.InvoiceNumberBookTypeId,
        ValorizationId: obj.ValorizationId,
        InvoicePointManual: obj.InvoicePoint,
        ServiceManual: obj.Service,
        VirtualWalletTransactionId: obj.VirtualWalletTransactionId,
        VirtualWalletOriginalAmount: obj.VirtualWalletOriginalAmount,
        Document: obj.Document,
      });

      if (obj.InvoiceTaxes && obj.InvoiceTaxes.length > 0) {
        obj.InvoiceTaxes.forEach((item) => this.addInvoiceTaxe(item));
      }

      if (obj.InvoiceCashDeskPaymentCash) {
        this.setInvoiceCashDeskPayment(
          obj.InvoiceCashDeskPaymentCash,
          <FormGroup>this.form.get("InvoiceCashDeskPaymentCash")
        );
      }

      if (obj.InvoiceCashDeskPaymentCheck) {
        this.setInvoiceCashDeskPayment(
          obj.InvoiceCashDeskPaymentCheck,
          <FormGroup>this.form.get("InvoiceCashDeskPaymentCheck")
        );
      }

      if (obj.InvoiceCashDeskPaymentCreditCard) {
        this.setInvoiceCashDeskPayment(
          obj.InvoiceCashDeskPaymentCreditCard,
          <FormGroup>this.form.get("InvoiceCashDeskPaymentCreditCard")
        );
      }

      if (obj.InvoiceCashDeskPaymentDebitCard) {
        this.setInvoiceCashDeskPayment(
          obj.InvoiceCashDeskPaymentDebitCard,
          <FormGroup>this.form.get("InvoiceCashDeskPaymentDebitCard")
        );
      }

      if (obj.InvoiceCashDeskPaymentWireTransfer) {
        this.setInvoiceCashDeskPaymentWireTransfer(
          obj.InvoiceCashDeskPaymentWireTransfer);
      }
      if (obj.InvoiceCashDeskPaymentQrLink) {
        this.setInvoiceCashDeskPaymentQrLink(obj.InvoiceCashDeskPaymentQrLink);
      }

      if (this.cashdeskuseropen) {
        this.invoicePointName = this.cashdeskuseropen.InvoicePoint.Name;
        this.filterCompleteService = new FilterCompleteService<ServiceFilter>(
          this.cashdeskuseropen.Services,
          this.form.controls.Service
        );
        this.filterCompleteInvoicePoint =
          new FilterCompleteService<InvoicePoint>(
            this.cashdeskuseropen.InvoicePoints,
            this.form.controls.InvoicePointManual
          );
        this.setFilterCompleteServiceManual();
        this.setCashdeskuseropen();
        this.setInvoicePointSelectManualType(
          this.cashdeskuseropen.InvoicePointSelectByService
        );
      }
      this.invoiceValueLoaded = true;
      this.trySetPaymentCashIfRequired();
    }
  }

  setInvoiceCashDeskPaymentWireTransfer(payment: InvoiceCashDeskPayment) {
    this.setInvoiceCashDeskPayment(
      payment,
      <FormGroup>this.form.get("InvoiceCashDeskPaymentWireTransfer")
    );
  }
  setInvoiceCashDeskPaymentQrLink(payment: InvoiceCashDeskPayment) {
    this.setInvoiceCashDeskPayment(
      payment,
      <FormGroup>this.form.get("InvoiceCashDeskPaymentQrLink")
    );
  }

  private setCashdeskuseropen() {
    this.form.patchValue({
      CashDeskInvoicePointId: this.cashdeskuseropen.CashDeskInvoicePointId,
      CashDeskOpenId: this.cashdeskuseropen.CashDeskOpen.Id,
      TaxNumber: this.cashdeskuseropen.InvoicePoint.TaxNumber,
      Service: null,
      InvoicePoint: this.cashdeskuseropen.InvoicePoint,
      InvoicePointManual: this.cashdeskuseropen.InvoicePoint,
    });

    if (this.invoiceCashDesk() && this.invoiceCashDesk().InvoiceId) {
      this.form.patchValue({
        ServiceManual: this.cashdeskuseropen.Service,
      });
    }
  }

  setInvoiceCashDeskPayment(
    invoiceCashDeskPayment: InvoiceCashDeskPayment,
    formGroup: FormGroup
  ) {
    formGroup.patchValue({
      PaymentAmount: invoiceCashDeskPayment.PaymentAmount.toFixed(2),
      BankFilter: invoiceCashDeskPayment.BankFilter,
      CreditCardFilter: invoiceCashDeskPayment.CreditCardFilter,
      CreditCardNumber: invoiceCashDeskPayment.CreditCardNumber,
      Coupon: invoiceCashDeskPayment.Coupon,
      Dues: invoiceCashDeskPayment.Dues,
      CheckNumber: invoiceCashDeskPayment.CheckNumber,
      TranseferNumber: invoiceCashDeskPayment.TranseferNumber,
      VirtualWalletFilter: invoiceCashDeskPayment.VirtualWalletFilter,
      VirtualWalletTransactionId:
        invoiceCashDeskPayment.VirtualWalletTransactionId,
    });
  }


  setTranseferNumber(TranseferNumber: string) {
    (<FormGroup>this.form.get("InvoiceCashDeskPaymentWireTransfer")).patchValue({
      TranseferNumber: TranseferNumber,
    });
  }
  setQrLinkTransferNumber(transferNumber: string) {
    (<FormGroup>this.form.get("InvoiceCashDeskPaymentQrLink")).patchValue({
      TranseferNumber: transferNumber,
    });
  }


  get isValid(): boolean {
    if (this.form && !this.form.invalid) {
      return true;
    } else {
      if (this.errors) {
        if (!this.form.controls.InvoiceCashDeskPaymentDebitCard.valid) {
          this.cashDeskPaymentFocus(1);
        } else if (!this.form.controls.InvoiceCashDeskPaymentCreditCard.valid) {
          this.cashDeskPaymentFocus(2);
        } else if (!this.form.controls.InvoiceCashDeskPaymentCheck.valid) {
          this.cashDeskPaymentFocus(3);
        } else if (
          !this.form.controls.InvoiceCashDeskPaymentWireTransfer.valid
        ) {
          this.cashDeskPaymentFocus(4);
        } else if (!this.form.controls.InvoiceCashDeskPaymentQrLink.valid) {
          this.cashDeskPaymentFocus(5);
        }
      }

      return false;
    }
  }

  getClientRequired(): boolean {
    const invoiceCashDesk = this.invoiceCashDesk();
    return invoiceCashDesk.InvoiceLabelType
      ? invoiceCashDesk.InvoiceLabelType.ClientRequired
      : false;
  }

  getTaxAmount(invoiceTax: InvoiceTax): number {
    const invoiceCashDesk = this.invoiceCashDesk();
    return new InvoiceCashDeskTotals(invoiceCashDesk).getTaxAmount(invoiceTax);
  }

  getTotalTaxAmount(): number {
    const invoiceCashDesk = this.invoiceCashDesk();
    return new InvoiceCashDeskTotals(invoiceCashDesk).getTotalTaxAmount();
  }

  getTotalAmount(): number {
    const invoiceCashDesk = this.invoiceCashDesk();
    return new InvoiceCashDeskTotals(invoiceCashDesk).getTotalAmount();
  }

  getTotalPayment(): number {
    const invoiceCashDesk = this.invoiceCashDesk();
    return new InvoiceCashDeskTotals(invoiceCashDesk).getTotalPayment();
  }

  getPaymentCardFormGroup(): FormGroup {
    return this.formBuilder.group(
      {
        PaymentAmount: [""],
        BankFilter: [null],
        VirtualWalletFilter: [null],
        CreditCardFilter: [null],
        CreditCardNumber: [""],
        Coupon: [""],
        Dues: [""],
        CheckNumber: [""],
        TranseferNumber: [""],
        VirtualWalletTransactionId: [""],
      },
      { validator: validPaymentCard.bind(this) }
    );
  }

  getPaymentCheckFormGroup(): FormGroup {
    return this.formBuilder.group(
      {
        PaymentAmount: [""],
        BankFilter: [null],
        VirtualWalletFilter: [null],
        CreditCardFilter: [null],
        CreditCardNumber: [""],
        Coupon: [""],
        Dues: [""],
        CheckNumber: [""],
        TranseferNumber: [""],
        VirtualWalletTransactionId: [""],
      },
      { validator: validPaymentCheck.bind(this) }
    );
  }

  getPaymentTransferFormGroup(): FormGroup {
    return this.formBuilder.group(
      {
        PaymentAmount: [""],
        BankFilter: [null],
        CreditCardFilter: [null],
        VirtualWalletFilter: [null],
        CreditCardNumber: [""],
        Coupon: [""],
        Dues: [""],
        CheckNumber: [""],
        TranseferNumber: [""],
        VirtualWalletTransactionId: [""],
      },
      { validator: validPaymentTransfer.bind(this) }
    );
  }

  getPaymentCashFormGroup(): FormGroup {
    return this.formBuilder.group({
      PaymentAmount: [""],
      BankFilter: [null],
      CreditCardFilter: [null],
      VirtualWalletFilter: [null],
      CreditCardNumber: [""],
      Coupon: [""],
      Dues: [""],
      CheckNumber: [""],
      TranseferNumber: [""],
      VirtualWalletTransactionId: [""],
    });
  }

  addInvoiceTaxe(invoiceTax: InvoiceTax): void {
    const row = this.formBuilder.group({
      Id: [invoiceTax.Id],
      Aliquots: [invoiceTax.Aliquots],
      Aliquot: [invoiceTax.Aliquot],
      AmountBase: [invoiceTax.AmountBase],
      Amount: [invoiceTax.Amount.toFixed(2)],
      TaxTypeId: [invoiceTax.TaxTypeId],
      TaxTypeName: [invoiceTax.TaxTypeName],
      InvoiceId: [invoiceTax.InvoiceId],
    });

    this.invoiceTaxes.push(row);
    this.invoiceTaxesDataSource.next(this.invoiceTaxes.controls);
  }

  displayAmountAliquots(index: number): string {
    return this.invoiceTaxes.controls[index].value.Aliquots;
  }

  displayAmount(index: number): number {
    const invoiceTax: InvoiceTax = this.invoiceTaxes.controls[index].value;
    return this.getTaxAmount(invoiceTax);
  }

  displayTaxTypeName(index: number): string {
    return this.invoiceTaxes.controls[index].value.TaxTypeName;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}

  aliquotChange(): void {
    this.trySetPaymentCashIfRequired();
  }

  private trySetPaymentCashIfRequired() {
    if (!this.paymentMethodsLoaded || !this.invoiceValueLoaded) {
      return;
    }

    if (
      !this.invoiceCashDeskPaymentCheck.PaymentAmount.value &&
      !this.invoiceCashDeskPaymentCreditCard.PaymentAmount.value &&
      !this.invoiceCashDeskPaymentDebitCard.PaymentAmount.value &&
      !this.invoiceCashDeskPaymentWireTransfer.PaymentAmount.value &&
      !this.invoiceCashDeskPaymentQrLink.PaymentAmount.value &&
      !this.invoiceCashDeskPaymentCash.PaymentAmount.value
    ) {
      const firstEnabledPaymentMethodFormGroupName =
        this.getFirstEnabledPaymentMethodFormGroupName();
      if (firstEnabledPaymentMethodFormGroupName) {
        (<FormGroup>(
          this.form.get(firstEnabledPaymentMethodFormGroupName)
        )).patchValue({
          PaymentAmount: this.getTotalAmount().toFixed(2),
        });
      }
    }
  }

  private updateFocusedPaymentAmount() {
    if (!this.paymentMethodsLoaded || !this.invoiceValueLoaded) {
      return;
    }

    const formGroupName =
      this.focusedPaymentMethodId === null
        ? this.getFirstEnabledPaymentMethodFormGroupName()
        : this.getPaymentMethodFormGroupName(this.focusedPaymentMethodId);

    if (formGroupName) {
      (<FormGroup>this.form.get(formGroupName)).patchValue({
        PaymentAmount: this.getTotalAmount().toFixed(2),
      });
    }
  }

  private getPaymentMethodFormGroupName(paymentMethodId: number): string | null {
    switch (paymentMethodId) {
      case 0:
        return "InvoiceCashDeskPaymentCash";
      case 1:
        return "InvoiceCashDeskPaymentDebitCard";
      case 2:
        return "InvoiceCashDeskPaymentCreditCard";
      case 3:
        return "InvoiceCashDeskPaymentCheck";
      case 4:
        return "InvoiceCashDeskPaymentWireTransfer";
      case 5:
        return "InvoiceCashDeskPaymentQrLink";
      default:
        return null;
    }
  }

  private getFirstEnabledPaymentMethodFormGroupName(): string | null {
    if (this.isPaymentMethodCashEnabled) {
      return "InvoiceCashDeskPaymentCash";
    } else if (this.isPaymentMethodDebitCardEnabled) {
      return "InvoiceCashDeskPaymentDebitCard";
    } else if (this.isPaymentMethodCreditCardEnabled) {
      return "InvoiceCashDeskPaymentCreditCard";
    } else if (this.isPaymentMethodCheckEnabled) {
      return "InvoiceCashDeskPaymentCheck";
    } else if (this.isPaymentMethodWireTransferEnabled) {
      return "InvoiceCashDeskPaymentWireTransfer";
    } else if (this.isPaymentMethodQrLinkEnabled) {
      return "InvoiceCashDeskPaymentQrLink";
    }
    return null;
  }

  private async loadPaymentMethodIds() {
    try {
      const paymentMethodIds = await this.centersService.getPaymentMethodIds(
        this.authService.getCenterId()
      );

      this.isPaymentMethodCashEnabled =
        paymentMethodIds.includes(PAYMENT_METHOD_ID_CASH);
      this.isPaymentMethodCreditCardEnabled = paymentMethodIds.includes(
        PAYMENT_METHOD_ID_CREDIT_CARD
      );
      this.isPaymentMethodDebitCardEnabled = paymentMethodIds.includes(
        PAYMENT_METHOD_ID_DEBIT_CARD
      );
      this.isPaymentMethodCheckEnabled =
        paymentMethodIds.includes(PAYMENT_METHOD_ID_CHECK);
      this.isPaymentMethodWireTransferEnabled = paymentMethodIds.includes(
        PAYMENT_METHOD_ID_WIRE_TRANSFER
      );
      this.isPaymentMethodQrLinkEnabled = paymentMethodIds.includes(
        PAYMENT_METHOD_ID_QR_LINK
      );

      this.paymentMethodsLoaded = true;
      this.changeDetectorRef.detectChanges();
      this.focusFirstEnabledPaymentMethod();
      this.trySetPaymentCashIfRequired();
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Médios de pago"
      );
    }
  }

  private focusFirstEnabledPaymentMethod() {
    if (this.isPaymentMethodCashEnabled) {
      if (this.cashPaymentAmountInput) {
        this.cashPaymentAmountInput.nativeElement.focus();
      }
      this.cashDeskPaymentFocus(0);
    } else if (this.isPaymentMethodDebitCardEnabled) {
      if (this.debitCardPaymentAmountInput) {
        this.debitCardPaymentAmountInput.nativeElement.focus();
      }
      this.cashDeskPaymentFocus(1);
    } else if (this.isPaymentMethodCreditCardEnabled) {
      if (this.creditCardPaymentAmountInput) {
        this.creditCardPaymentAmountInput.nativeElement.focus();
      }
      this.cashDeskPaymentFocus(2);
    } else if (this.isPaymentMethodCheckEnabled) {
      if (this.checkPaymentAmountInput) {
        this.checkPaymentAmountInput.nativeElement.focus();
      }
      this.cashDeskPaymentFocus(3);
    } else if (this.isPaymentMethodWireTransferEnabled) {
      if (this.wireTransferPaymentAmountInput) {
        this.wireTransferPaymentAmountInput.nativeElement.focus();
      }
      this.cashDeskPaymentFocus(4);
    } else if (this.isPaymentMethodQrLinkEnabled) {
      if (this.qrLinkPaymentAmountInput) {
        this.qrLinkPaymentAmountInput.nativeElement.focus();
      }
      this.cashDeskPaymentFocus(5);
    }
  }

  cashDeskPaymentFocus(cashDeskPaymentId: number) {
    this.focusedPaymentMethodId = cashDeskPaymentId;
    this.isVisiblePaymentDebitCard = false;
    this.isVisiblePaymentCreditCard = false;
    this.isVisiblePaymentCheck = false;
    this.isVisiblePaymentWireTransfer = false;
    this.isVisiblePaymentQrLink = false;

    if (cashDeskPaymentId === 1) {
      this.isVisiblePaymentDebitCard = true;
    } else if (cashDeskPaymentId === 2) {
      this.isVisiblePaymentCreditCard = true;
    } else if (cashDeskPaymentId === 3) {
      this.isVisiblePaymentCheck = true;
    } else if (cashDeskPaymentId === 4) {
      this.isVisiblePaymentWireTransfer = true;
    } else if (cashDeskPaymentId === 5) {
      this.isVisiblePaymentQrLink = true;
    }
  }

  totalText() {
    return this.getTotalAmount();
  }

  setInvoicePointSelectManualType(invoicePointSelectByService: boolean): void {
    this.invoicePointSelectManualTypes = [];
    const firstOption: InvoicePointSelectManualType =
      new InvoicePointSelectManualType("Punto de venta por servicio", 1);
    const seceondOption: InvoicePointSelectManualType =
      new InvoicePointSelectManualType("Selección manual punto de venta", 2);
    this.invoicePointSelectManualTypes.push(firstOption);
    this.invoicePointSelectManualTypes.push(seceondOption);

    this.form.patchValue({
      // InvoicePointSelectManualType: invoicePointSelectByService ? firstOption : seceondOption
      InvoicePointSelectManualType: firstOption,
    });
  }

  setFilterCompleteServiceManual(): void {
    this.filterService.getServicesFilter().then(
      (services) => {
        this.filterCompleteServiceManual =
          new FilterCompleteService<ServiceFilter>(
            services,
            this.form.controls.ServiceManual
          );
        if (this.form && !this.form.controls.ServiceManual.value) {
          this.form.patchValue({ ServiceManual: services[0] });
        }
      },
      (err) => {
        this.notificationToastService.error(
          JSON.stringify(err.error),
          "Cobranza"
        );
      }
    );
  }

  invoicePointSelectManualTypeChange(): void {
    if (this.form.controls.InvoicePointSelectManualType.value) {
      if (
        (<InvoicePointSelectManualType>(
          this.form.controls.InvoicePointSelectManualType.value
        )).Id === 2
      ) {
        this.form.patchValue({ Service: null });
      }
    }
  }

  async ngOnInit() {
    this.loadPaymentMethodIds();

    this.filterService.getBankFilters().then((bankFilters) => {
      this.bankFilters = bankFilters;

      this.filterCompleteBanksPaymentDebitCard =
        new FilterCompleteService<BankFilter>(
          this.bankFilters,
          this.invoiceCashDeskPaymentDebitCard.BankFilter
        );
      this.filterCompleteBanksPaymentCreditCard =
        new FilterCompleteService<BankFilter>(
          this.bankFilters,
          this.invoiceCashDeskPaymentCreditCard.BankFilter
        );
      this.filterCompleteBanksPaymentCheck =
        new FilterCompleteService<BankFilter>(
          this.bankFilters,
          this.invoiceCashDeskPaymentCheck.BankFilter
        );
    });

    this.filterService.getVirtualWalletFilters().then((virtualWallets) => {
      this.virtualWalletFilters = virtualWallets;

      this.filterCompleteVirtualWalletPaymentDebitCard =
        new FilterCompleteService<VirtualWalletFilter>(
          this.virtualWalletFilters,
          this.invoiceCashDeskPaymentDebitCard.VirtualWalletFilter
        );

      this.filterCompleteVirtualWalletPaymentCreditCard =
        new FilterCompleteService<VirtualWalletFilter>(
          this.virtualWalletFilters,
          this.invoiceCashDeskPaymentCreditCard.VirtualWalletFilter
        );

      this.filterCompleteVirtualWalletPaymentCheck =
        new FilterCompleteService<VirtualWalletFilter>(
          this.virtualWalletFilters,
          this.invoiceCashDeskPaymentCheck.VirtualWalletFilter
        );

      this.filterCompleteVirtualWalletPaymentTransfer =
        new FilterCompleteService<VirtualWalletFilter>(
          this.virtualWalletFilters,
          this.invoiceCashDeskPaymentWireTransfer.VirtualWalletFilter
        );
      this.filterCompleteVirtualWalletPaymentQrLink =
        new FilterCompleteService<VirtualWalletFilter>(
          this.virtualWalletFilters,
          this.invoiceCashDeskPaymentQrLink.VirtualWalletFilter
        );
    });

    this.filterService.getCreditCardFilters().then((creditCardFilters) => {
      this.creditCardFilters = creditCardFilters;
      this.filterCompleteCreditCardsPaymentDebitCard =
        new FilterCompleteService<CreditCardFilter>(
          this.creditCardFilters,
          this.invoiceCashDeskPaymentDebitCard.CreditCardFilter
        );
      this.filterCompleteCreditCardsPaymentCreditCard =
        new FilterCompleteService<CreditCardFilter>(
          this.creditCardFilters,
          this.invoiceCashDeskPaymentCreditCard.CreditCardFilter
        );
    });
  }

  async refreshInvoicePointName() {
    if (this.form.controls.InvoiceLabelType.value) {
      await this.invoiceLabelTypeSelected(
        this.form.controls.InvoiceLabelType.value
      );
    }
  }

  async invoiceLabelTypeSelected(invoiceLabelType: InvoiceLabelType) {
    try {
      this.loading = true;
      this.changeInvoiceCashDesk();
      if (invoiceLabelType && this.cashdeskuseropen) {
        this.invoiceCashDeskService
          .getNextNumber(
            this.cashdeskuseropen.CashDeskInvoicePointId,
            invoiceLabelType.Id
          )
          .then((invoiceNumberBookPoint) => {
            if (invoiceNumberBookPoint) {
              this.invoicePointName = `${this.cashdeskuseropen.InvoicePoint.Name}`;
            } else {
              this.invoicePointName = "";
            }
            this.setCashdeskuseropen();
            this.form.patchValue({
              InvoiceNumberBookId: invoiceNumberBookPoint
                ? invoiceNumberBookPoint.InvoiceNumberBookId
                : null,
              InvoiceNumberBookTypeId: invoiceNumberBookPoint
                ? invoiceNumberBookPoint.InvoiceNumberBookTypeId
                : null,
              Number: invoiceNumberBookPoint
                ? invoiceNumberBookPoint.Number
                : null,
            });
            this.changeInvoiceCashDesk();
            this.loading = false;
          });
      }
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Cobranza"
      );
    }
  }

  async taxpayerTypeSelected(taxpayerType: TaxpayerType) {
    this.form.patchValue({ InvoiceLabelType: null });

    if (taxpayerType) {
      const invoiceLabelTypeTaxInfo =
        await this.invoiceLabelService.getInvoiceLabelTypeAndTax(
          taxpayerType.Id
        );
      this.filterCompleteInvoiceLabelTypes =
        new FilterCompleteService<InvoiceLabelType>(
          invoiceLabelTypeTaxInfo.InvoiceLabelTypes,
          this.form.controls.InvoiceLabelType
        );
      this.form.patchValue({
        InvoiceLabelType: invoiceLabelTypeTaxInfo.InvoiceLabelTypes[0],
      });
      await this.invoiceLabelTypeSelected(
        invoiceLabelTypeTaxInfo.InvoiceLabelTypes[0]
      );
    }

    this.changeInvoiceCashDesk();
  }

  async serviceSelected(service: ServiceFilter) {
    await this.updateManualCashdeskuseropen(service ? service.Id : null, null);
  }

  async invoicePointChange(invoicePoint: InvoicePoint) {
    await this.updateManualCashdeskuseropen(
      null,
      invoicePoint ? invoicePoint.Id : null
    );
  }

  async updateManualCashdeskuseropen(
    serviceId?: number,
    invoicePointId?: number
  ) {
    try {
      this.loading = true;
      this.invoicePointName = "";
      this.form.patchValue({
        CashDeskInvoicePointId: null,
        InvoiceNumberBookId: null,
        InvoiceNumberBookTypeId: null,
        CashDeskOpenId: null,
        Number: null,
        TaxNumber: null,
        Service: null,
        InvoicePoint: null,
      });

      if (invoicePointId || serviceId) {
        this.cashdeskuseropen =
          await this.invoiceCashDeskService.getCashDeskUserOpen(
            false,
            false,
            null,
            serviceId,
            invoicePointId
          );
        if (
          this.cashdeskuseropen &&
          this.form.controls.InvoiceLabelType.valid
        ) {
          await this.invoiceLabelTypeSelected(
            this.form.controls.InvoiceLabelType.value
          );
        }
      } else {
        this.cashdeskuseropen = null;
      }
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Cobranza"
      );
    } finally {
      this.loading = false;
    }
  }
}
